import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Chip,
  FormControlLabel, Checkbox, Alert, Divider, IconButton, Grid, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  Select, FormControl, InputLabel, OutlinedInput, ListItemText,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Visibility as PreviewIcon,
  Refresh as RefreshIcon,
  PersonSearch as DiscoveryIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  previewContactDiscovery, startContactDiscovery, listContactDiscoveryRuns,
  deleteContactDiscoveryRun, getContactProviders, getIndustries,
} from '../services/api';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';

import { LEAD_STATUS_OPTIONS } from '../constants/leadStatus';
const STATUS_OPTIONS = LEAD_STATUS_OPTIONS.map(o => o.value);
const VERDICT_OPTIONS = ['CurrentFit', 'FuturePotential', 'NotAFit', 'Unknown'];
const FALLBACK_PROVIDERS = [
  { value: 'gemini', label: 'Gemini' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'hunterio', label: 'Hunter.io' },
];

const STATUS_CHIP_COLOR = {
  Pending: 'default',
  Running: 'info',
  Cancelling: 'warning',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

const defaultFilter = {
  leadIdsText: '',
  idFrom: '',
  idTo: '',
  industryIds: [],
  countries: '',
  countryCodes: '',
  verdicts: [],
  statuses: ['Qualified'],
  includeAlreadyDiscovered: false,
  limit: '',
};

function parseIdList(text) {
  if (!text) return null;
  const ids = text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isInteger(n) && n > 0);
  return ids.length > 0 ? ids : null;
}

function parseStringList(text) {
  if (!text) return null;
  const arr = text.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
  return arr.length > 0 ? arr : null;
}

function buildFilter(f) {
  return {
    leadIds: parseIdList(f.leadIdsText),
    idFrom: f.idFrom ? parseInt(f.idFrom, 10) : null,
    idTo: f.idTo ? parseInt(f.idTo, 10) : null,
    industryIds: f.industryIds && f.industryIds.length > 0 ? f.industryIds : null,
    countries: parseStringList(f.countries),
    countryCodes: parseStringList(f.countryCodes),
    verdicts: f.verdicts && f.verdicts.length > 0 ? f.verdicts : null,
    statuses: f.statuses && f.statuses.length > 0 ? f.statuses : null,
    includeAlreadyDiscovered: !!f.includeAlreadyDiscovered,
    limit: f.limit ? parseInt(f.limit, 10) : null,
  };
}

function fmtSecs(s) {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function fmtDuration(start, end, accumulatedSeconds) {
  const acc = accumulatedSeconds || 0;
  if (end) {
    if (acc > 0) return fmtSecs(acc);
    if (start) {
      const s = Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000));
      return fmtSecs(s);
    }
    return '—';
  }
  if (!start) return acc > 0 ? fmtSecs(acc) : '—';
  const live = Math.max(0, Math.round((Date.now() - new Date(start).getTime()) / 1000));
  return fmtSecs(acc + live);
}

export default function ContactDiscoveryPage() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [filter, setFilter] = useState(defaultFilter);
  const [industries, setIndustries] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState(['gemini', 'hunterio']);
  const [winnersPerLead, setWinnersPerLead] = useState('3');
  const [intervalSeconds, setIntervalSeconds] = useState('10');
  const [forceRediscover, setForceRediscover] = useState(false);
  const [preview, setPreview] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState({ preview: false, start: false });
  const [error, setError] = useState('');
  const [filterOpen, setFilterOpen] = useState(true);
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  const activeFilterCount = [
    parseIdList(filter.leadIdsText),
    filter.idFrom, filter.idTo,
    filter.industryIds && filter.industryIds.length > 0 ? '1' : null,
    filter.countries, filter.countryCodes,
    filter.verdicts && filter.verdicts.length > 0 ? '1' : null,
    // statuses default to ['Qualified']; only count when user changed it
    (filter.statuses && (filter.statuses.length !== 1 || filter.statuses[0] !== 'Qualified')) ? '1' : null,
    filter.limit,
  ].filter(Boolean).length + (filter.includeAlreadyDiscovered ? 1 : 0);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await listContactDiscoveryRuns(20);
      setRecent(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getContactProviders();
        const list = Array.isArray(data) ? data : [];
        setProviders(list);
      } catch { /* ignore */ }
      try {
        const { data } = await getIndustries();
        setIndustries(Array.isArray(data) ? data : (data?.items || []));
      } catch { /* ignore */ }
    })();
    loadRecent();
    const t = setInterval(loadRecent, 5000);
    return () => clearInterval(t);
  }, [loadRecent]);

  const providerOptions = providers.length > 0
    ? providers.map((p) => ({
        value: (p.name || '').toLowerCase(),
        label: p.displayName || p.name,
      }))
    : FALLBACK_PROVIDERS;

  const handlePreview = async () => {
    setLoading((s) => ({ ...s, preview: true }));
    setError('');
    try {
      const { data } = await previewContactDiscovery({
        filter: buildFilter(filter),
        providerNames: selectedProviders,
      });
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Preview failed');
    } finally {
      setLoading((s) => ({ ...s, preview: false }));
    }
  };

  const handleStart = async () => {
    if (selectedProviders.length === 0) {
      setError('Select at least one engine.');
      return;
    }
    const interval = intervalSeconds ? parseInt(intervalSeconds, 10) : 10;
    const winners = winnersPerLead ? parseInt(winnersPerLead, 10) : 3;
    const count = preview?.toProcessCount;
    const msg = count != null
      ? `Start contact discovery on ${count} leads using ${selectedProviders.join('+') || 'default'}? Winners per lead: ${winners}. Each lead processed with a ${interval}s gap.`
      : 'Start contact discovery on all matching leads (no preview)?';
    if (!(await dialog.confirm({ title: 'Start contact discovery', message: msg, confirmText: 'Start' }))) return;

    setLoading((s) => ({ ...s, start: true }));
    setError('');
    try {
      const { data } = await startContactDiscovery({
        filter: buildFilter(filter),
        providerNames: selectedProviders,
        winnersPerLead: winners,
        forceRediscover,
        intervalSeconds: interval,
      });
      navigate(`/leads/contact-discovery/${data.id}`);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to start contact discovery job');
    } finally {
      setLoading((s) => ({ ...s, start: false }));
    }
  };

  const handleDeleteRun = async (row) => {
    const ok = await dialog.confirm({
      title: 'Delete discovery job',
      message: `Delete job #${row.id}? This permanently removes the job and every per-lead result row. The leads themselves and their contacts are not affected.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteContactDiscoveryRun(row.id);
      await loadRecent();
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
    }
  };

  const previewColumns = [
    { key: 'id', header: 'ID', render: (v) => `#${v}` },
    { key: 'companyName', header: 'Company', render: (v) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography> },
    {
      key: 'website', header: 'Website', hideOnMobile: true,
      render: (v) => v ? <Typography variant="body2" sx={{ color: '#6366f1', wordBreak: 'break-all' }}>{v}</Typography> : <em style={{ color: '#94a3b8' }}>—</em>,
    },
    { key: 'status', header: 'Status', hideOnMobile: true },
    { key: 'aiIcpFit', header: 'Verdict', hideOnMobile: true, render: (v) => v || '—' },
    { key: 'country', header: 'Country', hideOnMobile: true, hideOnTablet: true, render: (v) => v || '—' },
    {
      key: 'contactsDiscoveredAt', header: 'Discovered', hideOnMobile: true, hideOnTablet: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : <em style={{ color: '#94a3b8' }}>never</em>,
    },
    { key: 'existingContactCount', header: 'Contacts', render: (v) => v ?? 0 },
  ];

  const recentColumns = [
    { key: 'id', header: 'ID', render: (v) => <Typography variant="body2" sx={{ fontWeight: 600 }}>#{v}</Typography> },
    {
      key: 'status', header: 'Status',
      render: (v) => <Chip size="small" label={v} color={STATUS_CHIP_COLOR[v] || 'default'} sx={{ fontWeight: 600 }} />,
    },
    {
      key: 'providerNames', header: 'Engines', hideOnMobile: true,
      render: (v) => Array.isArray(v) && v.length > 0
        ? <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{v.join('+')}</Typography>
        : <em style={{ color: '#94a3b8' }}>default</em>,
    },
    { key: 'winnersPerLead', header: 'Win/Lead', hideOnMobile: true, hideOnTablet: true },
    {
      key: 'startedAt', header: 'Started', hideOnMobile: true,
      render: (v) => v ? new Date(v).toLocaleString() : '—',
    },
    {
      key: 'duration', header: 'Duration', hideOnMobile: true,
      render: (_, row) => fmtDuration(row.startedAt, row.finishedAt, row.accumulatedSeconds),
    },
    { key: 'totalLeads', header: 'Leads', hideOnMobile: true, hideOnTablet: true },
    { key: 'processedLeads', header: 'Done' },
    {
      key: 'succeededLeads', header: 'OK', hideOnMobile: true, hideOnTablet: true,
      render: (v) => <Box component="span" sx={{ color: '#16a34a', fontWeight: 600 }}>{v}</Box>,
    },
    {
      key: 'failedLeads', header: 'Fail', hideOnMobile: true, hideOnTablet: true,
      render: (v) => <Box component="span" sx={{ color: v > 0 ? '#dc2626' : '#94a3b8', fontWeight: 600 }}>{v}</Box>,
    },
    {
      key: 'skippedLeads', header: 'Skip', hideOnMobile: true, hideOnTablet: true,
      render: (v) => <Box component="span" sx={{ color: v > 0 ? '#f59e0b' : '#94a3b8', fontWeight: 600 }}>{v}</Box>,
    },
    {
      key: 'contactsCreatedTotal', header: 'Contacts', hideOnMobile: true,
      render: (v) => <Box component="span" sx={{ color: '#0284c7', fontWeight: 700 }}>{v ?? 0}</Box>,
    },
    {
      key: 'createdByUsername', header: 'By', hideOnMobile: true, hideOnTablet: true,
      render: (v) => v || '—',
    },
    {
      key: 'actions', header: '',
      render: (_, row) => {
        const canDelete = ['Completed', 'Cancelled', 'Failed'].includes(row.status);
        return (
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small" variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/leads/contact-discovery/${row.id}`)}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              View
            </Button>
            {canDelete && (
              <Tooltip title="Delete job">
                <IconButton size="small" color="error" onClick={() => handleDeleteRun(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  const renderMultiChips = (selected) =>
    Array.isArray(selected) && selected.length > 0
      ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
          {selected.map((s) => <Chip key={s} size="small" label={s} sx={{ height: 20 }} />)}
        </Box>
      : <em style={{ color: '#94a3b8' }}>— Any —</em>;

  return (
    <Box>
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          <DiscoveryIcon color="primary" /> Auto Contact Discovery
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
          Find decision-maker contacts across qualified leads via selected AI engines + Hunter.io. Top contacts are scored, ranked, and flagged for outreach.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Button
          variant="contained" size="medium"
          startIcon={<AddIcon />}
          onClick={() => setNewDialogOpen(true)}
          sx={{
            textTransform: 'none', borderRadius: 1.5, fontWeight: 600,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 2px 6px rgba(99,102,241,0.35)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
          }}
        >
          New discovery job
        </Button>
        {activeFilterCount > 0 && (
          <Chip
            size="small"
            label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} set`}
            onClick={() => setNewDialogOpen(true)}
            sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 600, cursor: 'pointer' }}
          />
        )}
      </Stack>

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <DiscoveryIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>New contact discovery job</Typography>
            <Typography variant="caption" color="text.secondary">
              Pick a lead set, choose AI engines, and start.
            </Typography>
          </Box>
          <IconButton onClick={() => setNewDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
            <Stack
              direction="row" alignItems="center" spacing={1}
              onClick={() => setFilterOpen((o) => !o)}
              sx={{
                px: 2, py: 1.25, cursor: 'pointer', userSelect: 'none',
                bgcolor: filterOpen ? '#f8fafc' : 'transparent',
                '&:hover': { bgcolor: '#f8fafc' },
              }}
            >
              <FilterListIcon sx={{ fontSize: 18, color: '#475569' }} />
              <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 600 }}>Filter leads</Typography>
              {activeFilterCount > 0 && (
                <Chip size="small" label={activeFilterCount}
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#eef2ff', color: '#4338ca' }} />
              )}
              <Box sx={{ flex: 1 }} />
              {activeFilterCount > 0 && (
                <Button
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setFilter(defaultFilter); setPreview(null); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Clear
                </Button>
              )}
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setFilterOpen((o) => !o); }}
                sx={{ transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Collapse in={filterOpen} unmountOnExit>
              <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth size="small"
                      label="Lead IDs (comma/space-separated, optional)"
                      value={filter.leadIdsText}
                      onChange={(e) => setFilter((f) => ({ ...f, leadIdsText: e.target.value }))}
                      helperText="e.g. 1024, 1037, 2001 — leave empty to use other filters"
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    <TextField fullWidth size="small" type="number" label="ID from" value={filter.idFrom}
                      onChange={(e) => setFilter((f) => ({ ...f, idFrom: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                    <TextField fullWidth size="small" type="number" label="ID to" value={filter.idTo}
                      onChange={(e) => setFilter((f) => ({ ...f, idTo: e.target.value }))} />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Statuses (default Qualified)</InputLabel>
                      <Select
                        multiple
                        value={filter.statuses}
                        onChange={(e) => setFilter((f) => ({ ...f, statuses: e.target.value }))}
                        input={<OutlinedInput label="Statuses (default Qualified)" />}
                        renderValue={renderMultiChips}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <MenuItem key={s} value={s}>
                            <Checkbox size="small" checked={filter.statuses.includes(s)} />
                            <ListItemText primary={s} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Verdict (AiIcpFit)</InputLabel>
                      <Select
                        multiple
                        value={filter.verdicts}
                        onChange={(e) => setFilter((f) => ({ ...f, verdicts: e.target.value }))}
                        input={<OutlinedInput label="Verdict (AiIcpFit)" />}
                        renderValue={renderMultiChips}
                      >
                        {VERDICT_OPTIONS.map((v) => (
                          <MenuItem key={v} value={v}>
                            <Checkbox size="small" checked={filter.verdicts.includes(v)} />
                            <ListItemText primary={v} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Industries</InputLabel>
                      <Select
                        multiple
                        value={filter.industryIds}
                        onChange={(e) => setFilter((f) => ({ ...f, industryIds: e.target.value }))}
                        input={<OutlinedInput label="Industries" />}
                        renderValue={(selected) =>
                          selected.length > 0
                            ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                                {selected.map((id) => {
                                  const ind = industries.find((i) => i.id === id);
                                  return <Chip key={id} size="small" label={ind?.name || `#${id}`} sx={{ height: 20 }} />;
                                })}
                              </Box>
                            : <em style={{ color: '#94a3b8' }}>— Any —</em>}
                      >
                        {industries.map((ind) => (
                          <MenuItem key={ind.id} value={ind.id}>
                            <Checkbox size="small" checked={filter.industryIds.includes(ind.id)} />
                            <ListItemText primary={ind.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth size="small"
                      label="Countries (comma-separated)"
                      value={filter.countries}
                      onChange={(e) => setFilter((f) => ({ ...f, countries: e.target.value }))}
                      helperText="e.g. United States, Germany, France"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth size="small"
                      label="Country codes (comma-separated)"
                      value={filter.countryCodes}
                      onChange={(e) => setFilter((f) => ({ ...f, countryCodes: e.target.value }))}
                      helperText="e.g. US, DE, FR"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField fullWidth size="small" type="number" label="Limit" inputProps={{ min: 1, max: 5000 }}
                      value={filter.limit}
                      onChange={(e) => setFilter((f) => ({ ...f, limit: e.target.value }))} />
                  </Grid>
                </Grid>

                <FormControlLabel
                  control={<Checkbox size="small" checked={filter.includeAlreadyDiscovered}
                    onChange={(e) => setFilter((f) => ({ ...f, includeAlreadyDiscovered: e.target.checked }))} />}
                  label={<Typography variant="body2">Include leads with contacts already discovered</Typography>}
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#475569' }}>AI engines &amp; pacing</Typography>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Engines (default Gemini + Hunter)</InputLabel>
                      <Select
                        multiple
                        value={selectedProviders}
                        onChange={(e) => setSelectedProviders(e.target.value)}
                        input={<OutlinedInput label="Engines (default Gemini + Hunter)" />}
                        renderValue={renderMultiChips}
                      >
                        {providerOptions.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            <Checkbox size="small" checked={selectedProviders.includes(o.value)} />
                            <ListItemText primary={o.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                    <TextField fullWidth size="small" type="number" label="Winners per lead"
                      inputProps={{ min: 1, max: 10 }}
                      value={winnersPerLead}
                      onChange={(e) => setWinnersPerLead(e.target.value)}
                      helperText="Top-N flagged for outreach" />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                    <TextField fullWidth size="small" type="number" label="Interval (sec)"
                      inputProps={{ min: 0, max: 3600 }}
                      value={intervalSeconds}
                      onChange={(e) => setIntervalSeconds(e.target.value)}
                      helperText="Per-lead pacing" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4, md: 2 }} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={forceRediscover}
                        onChange={(e) => setForceRediscover(e.target.checked)} />}
                      label={<Typography variant="body2">Force re-discover</Typography>}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>
          {preview && (
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#475569' }}>
                  Preview: <strong>{preview.matchCount}</strong> matching · <strong>{preview.toProcessCount}</strong> to process
                  {preview.alreadyDiscoveredCount > 0 && <> · <span style={{ color: '#94a3b8' }}>{preview.alreadyDiscoveredCount} already discovered</span></>}
                </Typography>
              </Stack>
              {preview.hunterQuota && (
                <Alert
                  severity={preview.hunterQuota.willExceed ? 'error' : 'info'}
                  sx={{ mb: 1.5, py: 0.5 }}
                >
                  Hunter.io quota — available: <strong>{preview.hunterQuota.requestsAvailable}</strong>
                  {' · '}expected calls: <strong>{preview.hunterQuota.expectedCalls}</strong>
                  {preview.hunterQuota.resetDate && <> · resets {preview.hunterQuota.resetDate}</>}
                  {preview.hunterQuota.willExceed && <strong> — would exceed quota</strong>}
                </Alert>
              )}
              {preview.sample && preview.sample.length > 0 ? (
                <AzotteGrid
                  columns={previewColumns}
                  rows={preview.sample}
                  totalCount={preview.sample.length}
                  showPagination={false}
                  showToolbar={false}
                />
              ) : (
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>No leads match the filter.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap', '& .MuiButton-root': { borderRadius: 1.5, textTransform: 'none' } }}>
          <Button size="small" onClick={() => { setFilter(defaultFilter); setPreview(null); }}>
            Reset
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setNewDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="outlined" size="small" startIcon={<PreviewIcon />}
            onClick={handlePreview} disabled={loading.preview}>
            {loading.preview ? 'Counting…' : 'Preview match'}
          </Button>
          <Button variant="contained" size="small" startIcon={<PlayIcon />}
            onClick={handleStart} disabled={loading.start}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' } }}>
            {loading.start ? 'Starting…' : 'Start discovery'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', flex: 1 }}>Recent runs</Typography>
          <IconButton size="small" onClick={loadRecent} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
        </Stack>
        <AzotteGrid
          columns={recentColumns}
          rows={recent}
          totalCount={recent.length}
          showPagination={false}
          showToolbar={false}
          onDoubleClick={(r) => navigate(`/leads/contact-discovery/${r.id}`)}
          actionColumnKey="actions"
        />
      </Paper>
    </Box>
  );
}
